import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { useShipToApi } from "@/hooks/apiHooks/useShipToApi";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useProductFacetApi } from "@/hooks/apiHooks/useProductFacetApi";
import { categoriesAtom } from "@/store/categoryAtoms";
import { shipToLocationIdAtom } from "@/store/shipToAtoms";
import { buildCategoryChain, findCategory } from "@utils/categoryTree";
import { useSearchFilters } from "@customer/Search/useSearchFilters";
import { buildSearchUrl } from "@customer/Search/_helpers/buildSearchUrl";
import { useClampPageToRange } from "@customer/_helpers/useClampPageToRange";
import type { CategoryResponse } from "@models/category";
import type {
    FeaturedBannerProduct,
    FeaturedProduct,
    GetAllProductsResponse,
} from "@models/product";

// Same reason and same value as SearchResults: the price rail is a continuous
// control that writes the URL once per stop crossed, so the FETCH — never the
// URL — waits out a drag. The first fetch of a mount goes immediately.
const FILTER_FETCH_DEBOUNCE_MS = 250;

const EMPTY_RESPONSE: GetAllProductsResponse = {
    pageNumber: 1,
    totalPages: 0,
    count: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    items: [],
};

// The featured-product read (E4-S5). `failed` stays distinct from `loaded`
// with no product even though the banner renders the same fallback for both:
// "none" is the category's normal state, "failed" is a broken read.
export type FeaturedStatus = "idle" | "loading" | "loaded" | "failed";

interface FeaturedRead {
    status: FeaturedStatus;
    product: FeaturedProduct | undefined;
}

export interface UseBrowseSubcategoryResult {
    categoryChain: CategoryResponse[];
    currentCategory: CategoryResponse | undefined;
    featuredProduct: FeaturedBannerProduct | undefined;
    featuredStatus: FeaturedStatus;
    productPage: GetAllProductsResponse;
    isLoadingProducts: boolean;
    hasError: boolean;
    retry: () => void;
    pageNumber: number;
    setPage: (newPage: number) => void;
}

export const useBrowseSubcategory = (
    categoryId: number,
): UseBrowseSubcategoryResult => {
    const { callShipToApi } = useShipToApi();
    const { getCategories, getFeaturedProduct } = useCategoryApi();
    const { getProductFacets } = useProductFacetApi();
    const allCategories = useAtomValue(categoriesAtom);
    const shipToLocationId = useAtomValue(shipToLocationIdAtom);

    const [searchParams, setSearchParams] = useSearchParams();
    const pageNumber = Math.max(1, Number(searchParams.get("page")) || 1);

    // The #226 sort/filter surface, on the category page too (#401 B4). The
    // category is FIXED by the route: the panels hide their category chips,
    // and any ?category= in the URL is ignored in favour of the route's id.
    // GET /products expands categoryIds to descendants, so a parent category
    // page lists its whole subtree. (Before B4 the page sent a singular
    // `categoryId`, which GetProductsQuery does not bind — the read was
    // unfiltered.)
    const filters = useSearchFilters();
    // A plain string, so it is the stable key the fetch hangs off — the object
    // useSearchFilters returns is a fresh literal every render.
    const productsUrl = buildSearchUrl(undefined, pageNumber, {
        ...filters,
        categoryIds: [categoryId],
    });
    // The same request with the price cap taken out. Two consecutive reads
    // whose keys match therefore differ ONLY in maxPrice — which is the one
    // thing a drag on the price rail changes, and the only change the fetch
    // debounce is for. See the products effect below (#401 review R3).
    const readKey = buildSearchUrl(undefined, pageNumber, {
        ...filters,
        maxPrice: null,
        categoryIds: [categoryId],
    });

    const [productPage, setProductPage] =
        useState<GetAllProductsResponse>(EMPTY_RESPONSE);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const [hasError, setHasError] = useState(false);
    // One object so status and product can never disagree mid-render.
    const [featured, setFeatured] = useState<FeaturedRead>({
        status: "idle",
        product: undefined,
    });

    // The products state above is scoped to ONE category, and the route has
    // no `key`, so following a link to another category reuses this instance.
    // The reset happens here, during render, and not in an effect: an effect
    // is a commit too late, and that commit is exactly the one that renders
    // the previous category's cards under the new category's breadcrumb.
    // (#401 review R2.)
    //
    // `featured` is deliberately NOT reset here: the banner memo below already
    // withholds the banner while isLoadingProducts is true, which this block
    // raises in the same render, so a stale featured read cannot reach the
    // screen. Its own effect moves it to "loading" a commit later.
    //
    // Guarded on a FINITE id: `NaN !== NaN` is always true, so a non-numeric
    // route param would re-run this reset on every render and trip React's
    // re-render limit — the whole customer app, not one grid, would go to
    // GlobalError. BrowseSubcategory 404s such a param before calling this
    // hook; the guard keeps the hook safe on its own, and is the note for any
    // future render-phase reset keyed on a numeric route param.
    const [renderedCategoryId, setRenderedCategoryId] = useState(categoryId);
    if (Number.isFinite(categoryId) && renderedCategoryId !== categoryId) {
        setRenderedCategoryId(categoryId);
        setProductPage(EMPTY_RESPONSE);
        setIsLoadingProducts(true);
        setHasError(false);
    }

    useEffect(() => {
        if (allCategories.length === 0) {
            getCategories().catch(() => {
                // The breadcrumb and the page title degrade; the grid does
                // not depend on the tree. useApi surfaces the toast — without
                // this catch the rejection is unhandled (#401 review R9).
            });
        }
    }, [allCategories.length, getCategories]);

    // Brand + vendor chip values, once per DESTINATION per mount: the facets
    // are computed over the catalogue the destination can receive, so a
    // ship-to change re-reads them with the grid, or the chips would keep
    // describing the old destination. The ref — not the atom — is the guard,
    // and the catch releases it so one failure does not cost the shopper both
    // chip lists until a reload; the next category visited in this mount
    // tries again. No cleanup, deliberately — see SearchResults (#226 review
    // F1): a cleanup can cancel the read but never re-issue it.
    const facetsRequestedForRef = useRef<string | null>(null);
    const facetsKey = shipToLocationId ?? "";
    useEffect(() => {
        if (facetsRequestedForRef.current === facetsKey) return;
        facetsRequestedForRef.current = facetsKey;
        getProductFacets().catch(() => {
            // Secondary read; the grid and the other filters still work.
            facetsRequestedForRef.current = null;
        });
    }, [categoryId, facetsKey, getProductFacets]);

    useEffect(() => {
        // Latest wins: a categoryId change retires this read before it can
        // publish, and the effect run for the new id publishes in its place —
        // so, unlike a module-scope store, this per-mount flag can never
        // strand the status on "loading" (the only other exit is unmount).
        let superseded = false;
        setFeatured({ status: "loading", product: undefined });
        getFeaturedProduct(categoryId)
            .then((product) => {
                if (!superseded) setFeatured({ status: "loaded", product });
            })
            .catch((error: unknown) => {
                // The banner falls back to the first product (decorative
                // surface, no toast), so this log is the only trace the
                // failure leaves — it must not vanish behind a page that
                // looks fine.
                console.error(
                    `Featured product read failed for category ${categoryId}`,
                    error,
                );
                if (!superseded) {
                    setFeatured({ status: "failed", product: undefined });
                }
            });
        return () => {
            superseded = true;
        };
    }, [categoryId, getFeaturedProduct]);

    // The signal is REQUIRED, not optional: an unguarded `fetchProducts()` is
    // what let a retried answer overwrite a newer page, and `!signal?.cancelled`
    // hid it by being true whenever the argument was omitted. It no longer
    // compiles. (#401 review R4.)
    const fetchProducts = useCallback(
        async (signal: { cancelled: boolean }) => {
            try {
                const response =
                    await callShipToApi<GetAllProductsResponse>(productsUrl);
                if (!signal.cancelled) setProductPage(response);
            } catch {
                // useApi already surfaces the toast; flag the inline error too,
                // and drop the previous page's items: an ErrorState must not
                // sit under a banner built from a page that is no longer shown.
                if (!signal.cancelled) {
                    setProductPage(EMPTY_RESPONSE);
                    setHasError(true);
                }
            } finally {
                if (!signal.cancelled) setIsLoadingProducts(false);
            }
        },
        [productsUrl, callShipToApi],
    );

    // The one read allowed to write. Every path that starts one goes through
    // startRead, which supersedes whatever was in flight, and the effect's
    // cleanup cancels it — including on unmount. Retry is that same path, so
    // it can neither outlive the page nor land on top of a newer answer.
    const activeReadRef = useRef({ cancelled: true });
    const startRead = useCallback(() => {
        activeReadRef.current.cancelled = true;
        const signal = { cancelled: false };
        activeReadRef.current = signal;
        setIsLoadingProducts(true);
        setHasError(false);
        fetchProducts(signal);
    }, [fetchProducts]);

    const hasFetchedRef = useRef(false);
    const lastReadKeyRef = useRef(readKey);
    useEffect(() => {
        // The price rail is the page's only CONTINUOUS control, so it is the
        // only change that waits. A read whose key is unchanged differs from
        // the last one only in the price cap — a drag — and coalesces into
        // one request. Everything else is a click (a page, a chip, a category,
        // the first read of the mount) and is issued at once: charging a
        // discrete interaction 250 ms leaves the shopper looking at a grid
        // that has not started moving. (#401 review R3.)
        //
        // One seam, deliberately left: the first stop of a drag begun on page
        // 2+ also resets ?page, so it reads as discrete and issues its own
        // request. The rest of that drag still coalesces.
        const isPriceDrag =
            hasFetchedRef.current && readKey === lastReadKeyRef.current;
        hasFetchedRef.current = true;
        lastReadKeyRef.current = readKey;

        let timer: ReturnType<typeof setTimeout> | undefined;
        if (isPriceDrag) {
            // The fetch waits out the drag; the grid must not. Raised here,
            // in the interaction's own commit rather than inside the deferred
            // read, so the page does not sit there looking settled.
            setIsLoadingProducts(true);
            setHasError(false);
            timer = setTimeout(startRead, FILTER_FETCH_DEBOUNCE_MS);
        } else {
            startRead();
        }
        return () => {
            clearTimeout(timer);
            activeReadRef.current.cancelled = true;
        };
    }, [startRead, readKey]);

    // Retry is not a second mechanism: it is the same guarded, supersedable
    // read every other path uses (#401 review R4).
    const retry = startRead;

    const isClampPending = useClampPageToRange(
        pageNumber,
        productPage,
        isLoadingProducts,
    );

    const categoryChain = useMemo(
        () => buildCategoryChain(allCategories, categoryId),
        [allCategories, categoryId],
    );

    const currentCategory = useMemo(
        () => findCategory(allCategories, categoryId),
        [allCategories, categoryId],
    );

    const featuredProduct = useMemo<FeaturedBannerProduct | undefined>(() => {
        // No banner until the product page has settled with something to
        // show, whichever read lands first: a featured read that settles
        // early must not pop a banner over the grid's skeletons, and a page
        // with no products (empty category, out-of-range page, failed read)
        // gets none at all. That second clause is also #401 review R5 — the
        // banner must never sit above the grid's "No products found" — so the
        // review fix needs nothing added here; this is strictly stronger,
        // because it withholds the banner over the skeletons too — and over
        // the ErrorState, which a failed read raises with its items cleared.
        if (isLoadingProducts || hasError || productPage.items.length === 0) {
            return undefined;
        }
        // Withheld until the featured read settles too, so the page never
        // flashes the first product and then swaps it for the featured one.
        if (featured.status === "idle" || featured.status === "loading") {
            return undefined;
        }
        if (featured.product) return featured.product;
        // None (404) or a failed read: fall back to the first product so the
        // page keeps its designed shape; featuredStatus tells the two apart.
        return productPage.items[0];
    }, [featured, productPage.items, isLoadingProducts, hasError]);

    const setPage = useCallback(
        (newPage: number) => {
            const next = new URLSearchParams(searchParams);
            if (newPage <= 1) {
                next.delete("page");
            } else {
                next.set("page", String(newPage));
            }
            setSearchParams(next, { replace: false });
            if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        },
        [searchParams, setSearchParams],
    );

    return {
        categoryChain,
        currentCategory,
        featuredProduct,
        featuredStatus: featured.status,
        productPage,
        // The clamp reads the raw flag above; the page shows the skeleton
        // while a clamp is pending too (PR 395 review F1).
        isLoadingProducts: isLoadingProducts || isClampPending,
        hasError,
        retry,
        pageNumber,
        setPage,
    };
};
